import { useRef } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { IonContent, IonPage } from "@ionic/react";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import AppHeader from "../components/common/AppHeader";
import {
  SignInCard,
  Spinner,
  useAutoSilentSignIn,
} from "pymble-ui/components";
import AppFooter from "../components/common/AppFooter";
import AccessDenied from "../components/common/AccessDenied";
import FillerImage from "../components/common/FillerImage";
import { MainPageContainer } from "pymble-ui/components";
import MobileFooter from "../components/common/MobileFooter";
import { useCurrentWidth } from "pymble-ui/components";
import { useMyDetails } from "../hooks/useMyDetails";
import { api_s2 } from "../config";

import "./PymblePage.css";

// Screen width at which to switch from mobile to desktop layout
const MOBILE_SCREEN_WIDTH = 570;

// eslint-disable-next-line react-refresh/only-export-components
const AuthGate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isLoading, isError, hasAdminAccess } = useMyDetails();

  if (isLoading) return <Spinner message="Checking access..." />;
  if (isError) return <Spinner message="Error loading user details. Please refresh." />;
  if (!hasAdminAccess) return <AccessDenied />;

  return <>{children}</>;
};

interface PymblePageProps extends RouteComponentProps<any> {
  children?: any;
  cssClass?: string;
  hideFiller?: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
const PymblePage: React.FC<PymblePageProps> = ({
  children,
  cssClass,
  hideFiller,
  location,
}) => {
  const width = useCurrentWidth();
  const { instance, inProgress, accounts } = useMsal();
  const queryClient = useQueryClient();
  const { status: autoSignInStatus } = useAutoSilentSignIn({
    instance,
    inProgress,
    accounts,
    scopes: api_s2.scopes,
    redirectStartPage: `${location.pathname}${location.search}${location.hash}`,
  });

  const loginInProgress = useRef(false);
  const handleLogin = async () => {
    if (loginInProgress.current) return;
    loginInProgress.current = true;
    try {
      await instance.loginRedirect();
    } catch (error) {
      console.error("loginRedirect error:", error);
      loginInProgress.current = false;
    }
    queryClient.clear();
  };

  return (
    <IonPage className={cssClass ? cssClass : ""}>
      <AppHeader tools={width && width > MOBILE_SCREEN_WIDTH ? true : false} />
      <IonContent fullscreen className="ion-padding">
        <UnauthenticatedTemplate>
          {autoSignInStatus === "failed" ||
          autoSignInStatus === "skipped" ? (
            <SignInCard appName="Covers Manager" onLogin={handleLogin} />
          ) : (
            <Spinner
              message={
                autoSignInStatus === "attempting"
                  ? "Signing you in..."
                  : "Checking sign-in..."
              }
            />
          )}
        </UnauthenticatedTemplate>
        <AuthenticatedTemplate>
          <AuthGate>
            <MainPageContainer>{children}</MainPageContainer>
            {hideFiller ? null : <FillerImage />}
          </AuthGate>
        </AuthenticatedTemplate>
      </IonContent>
      <AuthenticatedTemplate>
        {width && width <= MOBILE_SCREEN_WIDTH ? <MobileFooter /> : null}
      </AuthenticatedTemplate>
      <AppFooter />
    </IonPage>
  );
};

export default withRouter(PymblePage);
